import * as THREE from 'three';

import { XR_CONFIG } from '../config.js';

const DEFAULT_RAY_COLOR = 0xf5d889;

function disposeObject(root) {
  root.traverse((object) => {
    object.geometry?.dispose?.();
    if (Array.isArray(object.material)) {
      for (let i = 0; i < object.material.length; i += 1) {
        object.material[i]?.dispose?.();
      }
    } else {
      object.material?.dispose?.();
    }
  });
}

function createProceduralControllerModel() {
  const model = new THREE.Group();
  const shellMaterial = new THREE.MeshStandardMaterial({
    color: 0xd7d8da,
    roughness: 0.72,
    metalness: 0.02,
  });
  const detailMaterial = new THREE.MeshStandardMaterial({
    color: 0x24272b,
    roughness: 0.62,
    metalness: 0.04,
  });
  const handle = new THREE.Mesh(
    new THREE.CapsuleGeometry(0.025, 0.065, 3, 8),
    shellMaterial,
  );
  const head = new THREE.Mesh(
    new THREE.SphereGeometry(0.038, 10, 7),
    shellMaterial,
  );
  const thumbstick = new THREE.Mesh(
    new THREE.CylinderGeometry(0.011, 0.013, 0.009, 10),
    detailMaterial,
  );

  model.name = 'XRControllerModel-Procedural';
  handle.rotation.x = -0.2;
  handle.position.set(0, -0.03, 0.025);
  head.scale.set(0.92, 0.55, 1.18);
  head.position.set(0, 0.018, -0.018);
  thumbstick.position.set(0, 0.045, -0.025);
  model.add(handle, head, thumbstick);
  return model;
}

/** Creates both Quest controller target rays and grip-space controller models. */
export class Controllers extends THREE.EventDispatcher {
  constructor(options = {}, positionalPlayerRig = null, positionalOptions = {}) {
    super();

    // Positional form is retained for small integrations:
    // new Controllers(renderer, playerRig, options).
    if (options?.isWebGLRenderer) {
      options = {
        ...positionalOptions,
        renderer: options,
        playerRig: positionalPlayerRig,
      };
    }

    const {
      renderer,
      playerRig,
      rayLength = XR_CONFIG?.rayLength ?? 8,
      rayColor = DEFAULT_RAY_COLOR,
      controllerModelFactory = null,
      onConnected = null,
      onDisconnected = null,
      onSelectStart = null,
      onSelectEnd = null,
      onSelect = null,
      onSqueezeStart = null,
      onSqueezeEnd = null,
    } = options;

    if (!renderer?.xr) throw new TypeError('Controllers requiere un WebGLRenderer válido.');
    if (!playerRig?.isObject3D) throw new TypeError('Controllers requiere playerRig.');

    this.renderer = renderer;
    this.playerRig = playerRig;
    this.rayLength = rayLength;
    this.rayColor = rayColor;
    this.states = [];
    this.controllers = [];
    this.grips = [];
    this._bindings = [];
    this._callbacks = {
      connected: onConnected,
      disconnected: onDisconnected,
      selectstart: onSelectStart,
      selectend: onSelectEnd,
      select: onSelect,
      squeezestart: onSqueezeStart,
      squeezeend: onSqueezeEnd,
    };

    this._rayGeometry = new THREE.BufferGeometry();
    this._rayGeometry.setAttribute('position', new THREE.Float32BufferAttribute([
      0, 0, 0,
      0, 0, -1,
    ], 3));

    for (let index = 0; index < 2; index += 1) {
      const controller = renderer.xr.getController(index);
      const grip = renderer.xr.getControllerGrip(index);
      const rayMaterial = new THREE.LineBasicMaterial({
        color: rayColor,
        transparent: true,
        opacity: 0.9,
        toneMapped: false,
      });
      const ray = new THREE.Line(this._rayGeometry, rayMaterial);
      // The default Three.js XRControllerModelFactory downloads input profiles
      // from a CDN. A local procedural model keeps the experience self-contained.
      const model = controllerModelFactory?.createControllerModel
        ? controllerModelFactory.createControllerModel(grip)
        : createProceduralControllerModel();
      const ownsModelResources = !controllerModelFactory;

      controller.name = `XRController-${index}`;
      grip.name = `XRControllerGrip-${index}`;
      ray.name = `XRControllerRay-${index}`;
      ray.scale.z = rayLength;
      ray.visible = false;
      ray.frustumCulled = false;
      ray.renderOrder = 10;

      controller.add(ray);
      grip.add(model);
      playerRig.add(controller, grip);

      const state = {
        index,
        controller,
        grip,
        ray,
        model,
        ownsModelResources,
        connected: false,
        selecting: false,
        squeezing: false,
        handedness: index === 0 ? 'left' : 'right',
        inputSource: null,
        gamepad: null,
        rayEnabled: true,
      };

      controller.userData.xrControllerState = state;
      this.states.push(state);
      this.controllers.push(controller);
      this.grips.push(grip);
      this._bindControllerEvents(state);
    }
  }

  _bindControllerEvents(state) {
    const eventNames = [
      'connected',
      'disconnected',
      'selectstart',
      'selectend',
      'select',
      'squeezestart',
      'squeezeend',
    ];

    for (let i = 0; i < eventNames.length; i += 1) {
      const eventName = eventNames[i];
      const handler = (event) => this._handleControllerEvent(eventName, state, event);
      state.controller.addEventListener(eventName, handler);
      this._bindings.push({ controller: state.controller, eventName, handler });
    }
  }

  _handleControllerEvent(type, state, originalEvent) {
    if (type === 'connected') {
      state.connected = true;
      state.inputSource = originalEvent.data ?? null;
      state.gamepad = state.inputSource?.gamepad ?? null;
      state.handedness = state.inputSource?.handedness || state.handedness;
      state.ray.visible = state.rayEnabled;
      state.grip.visible = true;
    } else if (type === 'disconnected') {
      state.connected = false;
      state.selecting = false;
      state.squeezing = false;
      state.inputSource = null;
      state.gamepad = null;
      state.ray.visible = false;
      state.grip.visible = false;
    } else if (type === 'selectstart') {
      state.selecting = true;
    } else if (type === 'selectend') {
      state.selecting = false;
    } else if (type === 'squeezestart') {
      state.squeezing = true;
    } else if (type === 'squeezeend') {
      state.squeezing = false;
    }

    this._callbacks[type]?.(state, originalEvent, this);
    this.dispatchEvent({ type, state, originalEvent });
  }

  update() {
    for (let i = 0; i < this.states.length; i += 1) {
      const state = this.states[i];
      state.gamepad = state.inputSource?.gamepad ?? null;
    }
  }

  getState(handOrIndex) {
    if (typeof handOrIndex === 'number') return this.states[handOrIndex] ?? null;
    return this.getByHand(handOrIndex);
  }

  getByHand(handedness) {
    let fallback = null;
    for (let i = 0; i < this.states.length; i += 1) {
      const state = this.states[i];
      if (state.handedness !== handedness) continue;
      if (state.connected) return state;
      fallback ??= state;
    }
    return fallback;
  }

  getController(handOrIndex) {
    return this.getState(handOrIndex)?.controller ?? null;
  }

  getGrip(handOrIndex) {
    return this.getState(handOrIndex)?.grip ?? null;
  }

  getGamepad(handOrIndex) {
    const state = this.getState(handOrIndex);
    return state?.inputSource?.gamepad ?? state?.gamepad ?? null;
  }

  setRayState(handOrIndex, { visible, length, color, opacity } = {}) {
    const state = this.getState(handOrIndex);
    if (!state) return false;

    if (visible !== undefined) {
      state.rayEnabled = visible;
      state.ray.visible = visible && state.connected;
    }
    if (length !== undefined) state.ray.scale.z = Math.max(0, length);
    if (color !== undefined) state.ray.material.color.set(color);
    if (opacity !== undefined) state.ray.material.opacity = opacity;
    return true;
  }

  setRaysVisible(visible) {
    for (let i = 0; i < this.states.length; i += 1) {
      this.setRayState(i, { visible });
    }
  }

  dispose() {
    for (let i = 0; i < this._bindings.length; i += 1) {
      const binding = this._bindings[i];
      binding.controller.removeEventListener(binding.eventName, binding.handler);
    }
    this._bindings.length = 0;

    for (let i = 0; i < this.states.length; i += 1) {
      const state = this.states[i];
      state.controller.remove(state.ray);
      state.grip.remove(state.model);
      state.controller.removeFromParent();
      state.grip.removeFromParent();
      state.ray.material.dispose();
      delete state.controller.userData.xrControllerState;
      if (state.ownsModelResources) disposeObject(state.model);
    }

    this._rayGeometry.dispose();
    this.states.length = 0;
    this.controllers.length = 0;
    this.grips.length = 0;
  }
}

export default Controllers;
